import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import axios from 'axios';
import {
  FaBold, FaItalic, FaLink, FaListUl, FaListOl, FaImage,
  FaHeading, FaUndo, FaRedo,
} from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Minimal TipTap-based rich text editor.
 *
 * Props:
 *   value       (string) — initial HTML
 *   onChange    (fn)     — called with updated HTML on every change
 *   uploadToTipId (string|null) — if set, image paste/upload uploads via
 *                                  /api/tips/{id}/upload-inline; if null,
 *                                  images fall back to a data-url until save
 *   placeholder (string) — placeholder text (empty state)
 *
 * Toolbar keeps things simple on purpose — H2/H3, bold, italic, lists,
 * link, image, undo/redo. No HTML view. Info-boxes come in Del 2.
 */
const TipTapEditor = ({ value, onChange, uploadToTipId, placeholder = 'Skriv din guide her…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // StarterKit inkluderer Link i nyere versioner; vi tilføjer vores
        // egen konfigurerede LinkExtension, så vi disabler den her for at
        // undgå "Duplicate extension names: [link]"-advarslen.
        link: false,
        // codeBlock/blockquote from starter-kit are fine defaults; keep them.
      }),
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank', class: 'text-cyan-700 underline' },
      }),
      ImageExtension.configure({
        HTMLAttributes: { class: 'rounded-lg max-w-full h-auto my-4' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose-base max-w-none focus:outline-none min-h-[280px] px-4 py-3',
        'data-testid': 'tiptap-editor-content',
      },
      // Kun billed-upload når udklipsholderen indeholder RENT billeddata
      // (fx et screenshot). Når brugeren kopierer formateret tekst fra
      // ChatGPT/Word/Google Docs, medfølger typisk et image/png thumbnail
      // SAMMEN MED text/html — så vi må lade TipTap's standard HTML-paste
      // tage over. Ellers går lister, afsnit og linjeskift tabt.
      handlePaste: (view, event) => {
        const cd = event.clipboardData;
        if (!cd) return false;
        const hasHtml  = !!cd.getData('text/html');
        const hasText  = !!cd.getData('text/plain');
        // Rig HTML/tekst → giv TipTap's default HTML-paste kontrol (bevarer
        // lister, overskrifter, afsnit, links, fed/kursiv).
        if (hasHtml || hasText) return false;

        // Ingen tekst — så det er formentlig et rent billed-paste.
        const files = cd.files;
        if (!files || files.length === 0) return false;
        const file = files[0];
        if (!file || !file.type.startsWith('image/')) return false;
        event.preventDefault();
        _uploadImage(file, uploadToTipId).then((url) => {
          if (url) editor?.chain().focus().setImage({ src: url, alt: file.name || '' }).run();
        });
        return true;
      },
      // Drag & drop images too — small quality-of-life bonus, matches paste.
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const file = files[0];
        if (!file.type.startsWith('image/')) return false;
        event.preventDefault();
        _uploadImage(file, uploadToTipId).then((url) => {
          if (url) editor?.chain().focus().setImage({ src: url, alt: file.name || '' }).run();
        });
        return true;
      },
    },
  });

  // Keep external value updates in sync (fx når editoren initialiseres efter
  // en async fetch af guiden).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') && value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const promptImage = useCallback(async () => {
    if (!editor) return;
    // Simple file-picker fallback (button in toolbar).
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const url = await _uploadImage(file, uploadToTipId);
      if (url) {
        const alt = window.prompt('Alt-tekst (kort beskrivelse af billedet — vigtig for tilgængelighed og SEO):', file.name || '');
        editor.chain().focus().setImage({ src: url, alt: alt || '' }).run();
      }
    };
    input.click();
  }, [editor, uploadToTipId]);

  const promptLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('URL (efterlad tom for at fjerne linket):', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return (
      <div className="border border-gray-300 rounded-lg p-4 text-gray-400" data-testid="tiptap-editor-loading">
        Indlæser editor…
      </div>
    );
  }

  const btnBase = 'p-2 rounded hover:bg-gray-100 transition-colors text-gray-700';
  const btnActive = 'bg-cyan-100 text-cyan-800';

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white" data-testid="tiptap-editor">
      <div className="flex flex-wrap gap-1 px-2 py-2 border-b border-gray-200 bg-gray-50" role="toolbar" aria-label="Tekst-formattering">
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`${btnBase} ${editor.isActive('heading', { level: 2 }) ? btnActive : ''}`}
                data-testid="tiptap-h2" title="Overskrift 2">
          <FaHeading /><sub className="text-xs">2</sub>
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                className={`${btnBase} ${editor.isActive('heading', { level: 3 }) ? btnActive : ''}`}
                data-testid="tiptap-h3" title="Overskrift 3">
          <FaHeading /><sub className="text-xs">3</sub>
        </button>
        <span className="w-px bg-gray-300 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}
                className={`${btnBase} ${editor.isActive('bold') ? btnActive : ''}`}
                data-testid="tiptap-bold" title="Fed">
          <FaBold />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`${btnBase} ${editor.isActive('italic') ? btnActive : ''}`}
                data-testid="tiptap-italic" title="Kursiv">
          <FaItalic />
        </button>
        <span className="w-px bg-gray-300 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`${btnBase} ${editor.isActive('bulletList') ? btnActive : ''}`}
                data-testid="tiptap-bullet" title="Punktliste">
          <FaListUl />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`${btnBase} ${editor.isActive('orderedList') ? btnActive : ''}`}
                data-testid="tiptap-ordered" title="Nummereret liste">
          <FaListOl />
        </button>
        <span className="w-px bg-gray-300 mx-1" />
        <button type="button" onClick={promptLink}
                className={`${btnBase} ${editor.isActive('link') ? btnActive : ''}`}
                data-testid="tiptap-link" title="Link">
          <FaLink />
        </button>
        <button type="button" onClick={promptImage} className={btnBase}
                data-testid="tiptap-image" title="Indsæt billede">
          <FaImage />
        </button>
        <span className="w-px bg-gray-300 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btnBase}
                data-testid="tiptap-undo" title="Fortryd" disabled={!editor.can().undo()}>
          <FaUndo />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btnBase}
                data-testid="tiptap-redo" title="Gentag" disabled={!editor.can().redo()}>
          <FaRedo />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────

async function _uploadImage(file, tipId) {
  // Inline billede — bruger det dedikerede /upload-inline endpoint som
  // ALDRIG rører guidens image_url (coverbilledet). Kræver et gemt tipId
  // fordi Cloudinary-uploads skal knyttes til en eksisterende guide for
  // ejerskabstjek og oprydning ved sletning.
  try {
    if (tipId) {
      const token = localStorage.getItem('session_token');
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/tips/${tipId}/upload-inline`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const raw = data?.url || '';
      if (!raw) return null;
      return raw.startsWith('http') ? raw : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
    }
  } catch (err) {
    console.error('[TipTap] inline image upload failed', err);
  }
  // Fallback for new (unsaved) guides: local blob URL — the image will render
  // in the editor but should be re-uploaded once the guide has an id.
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

export default TipTapEditor;
